import React from 'react';
import { BranchesOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Empty, Form, Input, Modal, Table, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { getTheme } from 'helpers/getTheme';
import { getVersionsColumns } from 'components/DetailsComponent/VersionsTab/columns/versionsColumns';
import { IColumn } from 'interfaces';
import dayjs from 'dayjs';
import cn from 'classnames';
import NewVersionModal from 'components/Modals/NewVersionModal/NewVersionModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import api from 'api';
import classes from './ModalNDAVersions.module.scss';
import { NDAVersion } from '../RoomAccess';

type ModalNDAVersionsProps = {
  open: boolean;
  onClose: () => void;
  versionsData: NDAVersion[];
  isLoadingVersions: boolean;
  fetchVersions: () => void;
};

const ModalNDAVersions = ({
  open,
  onClose,
  versionsData,
  isLoadingVersions,
  fetchVersions,
}: ModalNDAVersionsProps) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);
  const [choosenVersion, setChoosenVersion] = React.useState<NDAVersion | null>(null);
  const [isOpenRollbackModal, setIsOpenRollbackModal] = React.useState(false);
  const [isRollbacking, setIsRollbacking] = React.useState(false);
  const [form] = Form.useForm();

  const { t, i18n } = useTranslation();
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const dispatch = useAppDispatch();

const renderCell = (props: { value: any; info: NDAVersion; col: IColumn }, t: Function) => {
  const { value, info, col } = props;

  if (col.key === 'name') {
    const ext = info.name?.split('.')?.reverse()[0];
    const icon = availableIconFormat.includes(ext) ? ext : 'default';
    return (
      <div className={classes.rowWrap}>
        <div className={classes.icon}>
          <Avatar
            className={classes.avatar}
            icon={documnentIcons[icon as keyof typeof documnentIcons]}
          />
        </div>
        {value}
      </div>
    );
  }

  if (col.key === 'upload_at') {
    return info.date !== '-' ? dayjs.utc(info.date).local().format('DD-MM-YYYY HH:mm:ss') : '-';
  }

  if (col.key === 'author') {
    return value
      ? <div><UserOutlined className={classes.authorIcon} /><span className={classes.authorName}>{value}</span></div>
      : '-';
  }

  if (col.key === 'version_comment') {
    return (
      <div className={classes.commentWrap}>
        {value || '-'}
      </div>
    );
  }

  if (col.key === 'details') {
    if (info.is_current) {
      return <Tag className={classes.current}>{t('Settings.terms.versions.current')}</Tag>;
    }

    return (
      <div className={classes.details}>
        <Tooltip placement='topRight' title={t('Settings.terms.versions.rollback')}>
          <Button
            className={classes.iconButton}
            size='small'
            type='text'
            icon={<SwapOutlined className={classes.rollbackIcon} />}
            onClick={() => {
              setChoosenVersion(info);
              setIsOpenRollbackModal(true);
            }}
          />
        </Tooltip>
      </div>
    );
  }

  return value;
};

  React.useEffect(() => {
    const cols = getVersionsColumns(t);
    const customColumns = cols.map((col: IColumn) => ({
      ...col,
      render: (value: any, info: NDAVersion) => renderCell({ value, info, col }, t),
    }));
    setModifiedColumns(customColumns);
  }, [versionsData, i18n.language]);

  const closeRollbackModal = () => {
    setIsOpenRollbackModal(false);
    setChoosenVersion(null);
    form.resetFields(['comment']);
  };

  const onRollback = async () => {
    if (!dataRoom?.id || !choosenVersion) return;
    setIsRollbacking(true);
    try {
      const response = await api.ndaVersionReset({
        room_id: dataRoom.id,
        version_id: choosenVersion.id,
        comment: form.getFieldValue('comment') || '',
      });
      dispatch(setDataRoom({
        ...dataRoom,
        agreement_id: response.data.id,
        agreement_file: response.data.name,
      }));
      messageApi?.success(t('Settings.terms.versions.rollbackSuccess'));
      fetchVersions();
    } catch {
      messageApi?.error(t('Settings.terms.versions.rollbackError'));
    } finally {
      setIsRollbacking(false);
      closeRollbackModal();
    }
  };

  const rollbackText = (
    <div>
      <div className={classes.confirmText}>{t('Settings.terms.versions.rollbackConfirmText')}</div>
      <div className={classes.confirmDocCard}>
        {choosenVersion?.name}
      </div>
      <Form form={form} layout='vertical' className={classes.rollbackForm}>
        <Form.Item
          label={<span className={classes.commentLabel}>{t('Settings.terms.versions.rollbackCommentLabel')}</span>}
          name='comment'
        >
          <Input.TextArea
            maxLength={500}
            showCount
            placeholder={t('Settings.terms.versions.rollbackCommentPlaceholder')}
            autoSize={{ maxRows: 10, minRows: 4 }}
          />
        </Form.Item>
      </Form>
    </div>
  );

  const modalTitle = (
    <div className={classes.modalTitle}>
      <BranchesOutlined className={classes.titleIcon} />
      {t('Settings.terms.versions.title')}
    </div>
  );

  const footer = (
    <div className={classes.footer}>
      <Button
        type='primary'
        onClick={() => setIsUploadModalOpen(true)}
      >
        {t('Settings.terms.versions.uploadNew')}
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        title={modalTitle}
        centered
        width={1000}
        onCancel={onClose}
        className={cn(classes.modal, getTheme())}
        footer={footer}
      >
        <Table
          size='small'
          columns={modifiedColumns}
          dataSource={versionsData}
          pagination={false}
          loading={isLoadingVersions}
          className={classes.table}
          rowClassName={classes.hoverable}
          locale={{
            emptyText: (
              <Empty
                description={t('Settings.terms.versions.noVersions')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Modal>
      {isUploadModalOpen && (
        <NewVersionModal
          isOpen={isUploadModalOpen}
          onCancel={() => setIsUploadModalOpen(false)}
          isNDA
          entity={{
            name: versionsData[0]?.name || t('Settings.terms.versions.title'),
          }}
          onSuccess={fetchVersions}
        />
      )}
      {isOpenRollbackModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={isOpenRollbackModal}
            onCancel={closeRollbackModal}
            isLoading={isRollbacking}
            submit={onRollback}
            text={rollbackText}
            title={t('Settings.terms.versions.rollbackTitle')}
            okText={t('Settings.terms.versions.rollback')}
            customClassname={'restore'}
          />
        </React.Suspense>
      )}
    </>
  );
};

export default ModalNDAVersions;
